// Helper function to get nested value
const getNestedValue = (obj, path) => {
  return path.reduce((current, key) => current?.[key], obj)
}

// Helper function to set nested value with Immer
const setNestedValue = (obj, path, value) => {
  let current = obj
  for (let i = 0; i < path.length - 1; i++) {
    if (!current[path[i]]) current[path[i]] = {}
    current = current[path[i]]
  }
  current[path[path.length - 1]] = value
}

export const deleteItem = (state, statePath) => {
  const item = getNestedValue(state, statePath)
  
  if (item && parseInt(item.id)) {
    // Mark for deletion instead of removing
    setNestedValue(state, statePath, { ...item, _destroy: '1' })
  } else {
    // Actually remove the item
    let current = state
    const pathToParent = statePath.slice(0, -1)
    const lastKey = statePath[statePath.length - 1]
    
    for (const key of pathToParent) {
      current = current[key]
      if (!current) return
    }
    
    if (Array.isArray(current)) {
      current.splice(lastKey, 1)
    } else {
      delete current[lastKey]
    }
  }
}

export const getFullPath = (path) => {
  const [rowIndex, cellIndex, elementPosition] = path
  let resultPath = ['data']

  if (rowIndex !== null && rowIndex !== undefined) { 
    resultPath.push('rows', rowIndex)
  }
  if (cellIndex !== null && cellIndex !== undefined) { 
    resultPath.push('cells', cellIndex)
  }
  if (elementPosition !== null && elementPosition !== undefined) {
    resultPath.push('elements', elementPosition)
  }

  return resultPath
}

const getTargetElementPath = (toFullPath, isCellPath) => {
  if (isCellPath) { 
    return [...toFullPath, 'elements']
  }
  return toFullPath.slice(0, -1)
}

export class ElementTransporter {
  constructor (state) {
    this._state = state
  }

  move = (action) => {
    if (action.fromPath.length < 3 || action.toPath.length < 2) { 
      return
    }

    const options = {
      fromPath: action.fromPath, 
      toPath: action.toPath,
      fromFullPath: getFullPath(action.fromPath), 
      toFullPath: getFullPath(action.toPath),
    }
    options.targetElementsPath = getTargetElementPath(options.toFullPath, options.toPath.length == 2)

    if (action.fromPath[0] == action.toPath[0] && action.fromPath[1] == action.toPath[1]) {
      this.changePosition(options)
    }
    else if (action.toPath.length == 2) { 
      this.moveToAnotherCell(options)
    }
    else if (action.toPath.length == 3) { 
      this.moveToAnotherCellToSpecificPosition(options)
    }
  }

  moveToAnotherCell = ({ fromFullPath, targetElementsPath }) => {
    const element = this.deleteElement(fromFullPath)
    if (!element) return
    
    const targetElements = getNestedValue(this._state, targetElementsPath) || []
    const { elements, deletedElements } = this.splitElements(targetElements)
    
    setNestedValue(this._state, targetElementsPath, [...elements, element, ...deletedElements])
  }

  moveToAnotherCellToSpecificPosition = ({ fromFullPath, toFullPath, targetElementsPath }) => {
    const element = this.deleteElement(fromFullPath)
    if (!element) return
    
    const targetElements = getNestedValue(this._state, targetElementsPath) || []
    const { elements, deletedElements } = this.splitElements(targetElements)
    const elementPosition = toFullPath[toFullPath.length - 1]
    
    elements.splice(elementPosition, 0, element)
    setNestedValue(this._state, targetElementsPath, [...elements, ...deletedElements])
  }

  changePosition = ({ fromPath, toPath, fromFullPath, targetElementsPath }) => {
    const element = this.deleteElement(fromFullPath, true)
    if (!element) return
    
    const targetElements = getNestedValue(this._state, targetElementsPath) || []
    const { elements, deletedElements } = this.splitElements(targetElements)
    const newPos = toPath[toPath.length - 1]
    
    elements.splice(newPos, 0, element)
    setNestedValue(this._state, targetElementsPath, [...elements, ...deletedElements])
  }

  splitElements = (allElements) => {
    const elements = []
    const deletedElements = []
    
    allElements.forEach(el => {
      if (el._destroy) {
        deletedElements.push(el)
      } else {
        elements.push(el)
      }
    })
    
    return { elements, deletedElements }
  }

  deleteElement = (deletePath, force = false) => {
    const element = getNestedValue(this._state, deletePath)
    if (!element) return null
    
    if (element.id && !force) {
      // Mark for deletion
      setNestedValue(this._state, deletePath, { ...element, _destroy: true })
      // Return copy without id and _destroy
      const { id, _destroy, ...elementCopy } = element
      return elementCopy
    } else {
      // Actually remove the element
      let current = this._state
      const pathToParent = deletePath.slice(0, -1)
      const lastKey = deletePath[deletePath.length - 1]
      
      for (const key of pathToParent) {
        current = current[key]
        if (!current) return null
      }
      
      const removedElement = current[lastKey]
      if (Array.isArray(current)) {
        current.splice(lastKey, 1)
      } else {
        delete current[lastKey]
      }
      
      return removedElement
    }
  }
}

export const moveRowItem = (state, action) => {
  if (action.newPos < 0) return
  
  const rows = state.data?.rows
  if (!rows || !rows[action.oldPos]) return
  
  const row = rows[action.oldPos]
  rows.splice(action.oldPos, 1)
  rows.splice(action.newPos, 0, row)
}
